/*
 * Warped Tour landing-page analytics (#325).
 *
 * The single, page-wide Amplitude taxonomy pass for the Warped Tour Long Beach landing
 * page. Loaded once by sections/warped-tour-hero.liquid (the hero is guaranteed present
 * and owns the phase word), then attaches DOCUMENT-scoped delegated listeners so it covers
 * every band regardless of section order.
 *
 * Why hand-sent: the Shopify-bundled Amplitude SDK ignores dataAttributePrefix autocapture,
 * so events go through amplitude.track('Event Name', {...}) by hand (see CLAUDE.md Analytics
 * + assets/variant-picker.js). Event names are Title Case to sit alongside 'Variant Selected'.
 *
 * Taxonomy is documented in docs/warped-tour-analytics-taxonomy.md. Interaction firing is
 * guarded by tests/e2e/local/wt-analytics.spec.js (the accessor-stub pattern — the real app
 * SDK mutates window.amplitude in place, so a plain object stub captures nothing).
 *
 * Reuses the data-wt-cta / data-cal-type hooks already rendered by #317/#319/#320/#321 —
 * no markup was restructured to track. The reused `add_calendar` / `directions` hooks are
 * disambiguated by the derived `surface` + `phase` props, not by distinct hook names.
 */
(function () {
  // Idempotency: this KPI-accuracy module must attach its listeners exactly once. If the hero
  // section were ever duplicated in the theme editor, the loader tag would render twice and
  // double-count every event. Guard so a second load is a no-op.
  if (/** @type {any} */ (window).__wtAnalyticsLoaded) return;
  /** @type {any} */ (window).__wtAnalyticsLoaded = true;

  var amp = function () {
    var a = /** @type {any} */ (window).amplitude;
    return a && typeof a.track === 'function' ? a : null;
  };

  var CAMPAIGN = 'warped_tour_2026';

  // Phase (pre|event|post) is server-rendered onto the hero root by snippets/warped-tour-phase.liquid.
  var phaseEl = document.querySelector('[data-wt-root]');
  var PHASE = (phaseEl && phaseEl.getAttribute('data-phase')) || 'unknown';

  // Which band a node sits in — derived from the existing section marker attributes, so no
  // markup changed. Disambiguates the hooks that repeat across bands (directions, add_calendar).
  // Each row is [attribute, surface]. surface is a FIXED name, except the value-driven email
  // band (null): data-wt-surface carries its own surface name as the attribute value, so the
  // shared neuro-email-signup section names itself per placement (blank ⇒ attr not rendered ⇒
  // not a surface, e.g. the homepage instance).
  var SURFACE_MARKERS = [
    ['data-wt-root', 'hero'],
    ['data-wt-cause', 'cause'],
    ['data-wt-decks', 'decks'],
    ['data-wt-venue', 'venue'],
    ['data-wt-findus', 'find_us'],
    ['data-wt-match', 'online_match'],
    ['data-wt-contest', 'contest'],
    ['data-wt-faq', 'faq'],
    ['data-wt-surface', null],
  ];
  var SURFACE_SELECTOR = SURFACE_MARKERS
    .map(function (m) {
      return '[' + m[0] + ']';
    })
    .join(',');

  /** @param {Element} el */
  function surfaceOf(el) {
    var container = el.closest ? el.closest(SURFACE_SELECTOR) : null;
    if (!container) return 'unknown';
    for (var i = 0; i < SURFACE_MARKERS.length; i++) {
      if (container.hasAttribute(SURFACE_MARKERS[i][0])) {
        // A null fixed-name is value-driven: the surface is the attribute's own value.
        return SURFACE_MARKERS[i][1] || container.getAttribute(SURFACE_MARKERS[i][0]) || 'unknown';
      }
    }
    return 'unknown';
  }

  // Internal-traffic + environment tagging (mirrors sections/brady-event.liquid) so charts can
  // filter to environment=production + traffic_type=external.
  var enrich = (function () {
    var host = (window.location.hostname || '').toLowerCase();
    var environment = 'production';
    if (host === '127.0.0.1' || host === 'localhost') environment = 'local';
    else if (host.indexOf('shopifypreview.com') !== -1 || host.indexOf('.myshopify.com') !== -1)
      environment = 'preview';
    var internal = false;
    try {
      internal =
        navigator.webdriver === true ||
        (window.localStorage && localStorage.getItem('neuro_internal') === '1') ||
        environment !== 'production';
    } catch (e) {
      internal = navigator.webdriver === true || environment !== 'production';
    }
    return { environment: environment, traffic_type: internal ? 'internal' : 'external' };
  })();

  // UTM params — campaign attribution for the booth email / socials links.
  var utm = {};
  try {
    var sp = new URLSearchParams(window.location.search);
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
      var v = sp.get(k);
      if (v) utm[k] = v;
    });
  } catch (e) {}

  /**
   * Every WT event carries the campaign context + phase + enrichment + any UTM params.
   * @param {Record<string, any>} [props]
   */
  function track(name, props) {
    var a = amp();
    if (!a) return;
    var merged = {
      campaign: CAMPAIGN,
      phase: PHASE,
      environment: enrich.environment,
      traffic_type: enrich.traffic_type,
    };
    var k;
    for (k in utm) {
      if (Object.prototype.hasOwnProperty.call(utm, k)) merged[k] = utm[k];
    }
    if (props) {
      for (k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k)) merged[k] = props[k];
      }
    }
    try {
      a.track(name, merged);
    } catch (e) {}
  }

  // ---- Page view ----
  track('WT Landing Page Viewed', { page_location: window.location.href });

  // ---- First-touch user properties (set once, never overwritten) ----
  (function () {
    var a = /** @type {any} */ (window).amplitude;
    if (!a || typeof a.Identify !== 'function' || typeof a.identify !== 'function') return;
    try {
      var id = new a.Identify();
      id.setOnce('first_wt_utm_source', utm.utm_source || '(direct)');
      id.setOnce('first_wt_utm_campaign', utm.utm_campaign || '(none)');
      id.setOnce('first_wt_referrer', document.referrer || '(direct)');
      id.set('environment', enrich.environment);
      id.set('traffic_type', enrich.traffic_type);
      a.identify(id);
    } catch (e) {}
  })();

  // ---- Section viewed (>=50% once each) ----
  if ('IntersectionObserver' in window) {
    var seen = {};
    var obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var vh = window.innerHeight || document.documentElement.clientHeight;
          var coversHalfViewport = entry.intersectionRect.height >= vh * 0.5;
          if (!entry.isIntersecting || (entry.intersectionRatio < 0.5 && !coversHalfViewport)) return;
          var name = surfaceOf(entry.target);
          if (!seen[name]) {
            seen[name] = true;
            track('WT Section Viewed', { section_name: name });
          }
          obs.unobserve(entry.target);
        });
      },
      { threshold: [0, 0.25, 0.5, 0.75] }
    );
    document.querySelectorAll(SURFACE_SELECTOR).forEach(function (el) {
      obs.observe(el);
    });
  }

  // ---- CTA / calendar / deck clicks (capture phase, before navigation) ----
  /** @param {Event} e */
  function handleClick(e) {
    var target = /** @type {Element} */ (e.target);
    if (!target || !target.closest) return;

    // Calendar add is the pre-event KPI. Fire and return so the same click isn't also
    // counted as a generic CTA (the calendar links carry BOTH data-cal-type and data-wt-cta).
    var calLink = target.closest('[data-cal-type]');
    if (calLink) {
      track('WT Calendar Add Clicked', {
        calendar_type: calLink.getAttribute('data-cal-type'),
        surface: surfaceOf(calLink),
      });
      return;
    }

    var ctaLink = target.closest('[data-wt-cta]');
    if (ctaLink) {
      track('WT CTA Clicked', {
        cta: ctaLink.getAttribute('data-wt-cta'),
        surface: surfaceOf(ctaLink),
        destination_url: /** @type {HTMLAnchorElement} */ (ctaLink).href || null,
      });
      return;
    }

    // Deck-grid per-deck interest. The canonical product cards carry no data-wt-cta and fire
    // no tracking of their own, so this is the only deck-click signal (no double-count).
    var item = target.closest('.wt-decks__item');
    if (item) {
      // Scoped to the card's product link + the quick-add element — NOT a bare `button`, which
      // would fire on any unrelated button the canonical `_product-card` preset might grow (e.g.
      // a swatch picker). Re-verify this selector if the product-card / quick-add markup changes.
      var interactive = target.closest('a[href*="/products/"], quick-add-component, .quick-add');
      if (!interactive) return;
      var anchor = item.querySelector('a[href*="/products/"]');
      var qa = item.querySelector('quick-add-component');
      track('WT Deck Clicked', {
        product_handle: anchor ? handleFromHref(anchor.getAttribute('href')) : null,
        product_title: (qa && qa.getAttribute('data-product-title')) || (anchor ? anchor.textContent.trim() : null),
        deck_interaction: target.closest('quick-add-component, .quick-add') ? 'quick_add' : 'view',
      });
    }
  }

  /** @param {string | null} href → the product handle segment of a /products/<handle> path. */
  function handleFromHref(href) {
    if (!href) return null;
    var path = href;
    try {
      path = new URL(href, window.location.origin).pathname;
    } catch (e) {}
    var m = path.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // ---- FAQ accordion expand ----
  // Native <details> `toggle` does NOT bubble, so listen in the CAPTURE phase (which still
  // reaches document for a non-bubbling event). Fire only on EXPAND (open), never on collapse.
  /** @param {Event} e */
  function handleToggle(e) {
    var detailsEl = /** @type {Element} */ (e.target);
    if (!detailsEl || !detailsEl.hasAttribute || !detailsEl.hasAttribute('data-wt-faq-item')) return;
    if (!(/** @type {HTMLDetailsElement} */ (detailsEl).open)) return;
    track('WT FAQ Expanded', {
      question: detailsEl.getAttribute('data-wt-faq-q'),
      surface: surfaceOf(detailsEl),
    });
  }

  // ---- Email capture ----
  // The Shopify customer form does a full-page POST; the capture-phase submit fires first, so
  // the event is sent before navigation. HTML5 `required` + type=email means an invalid address
  // blocks submit entirely, so only a valid capture attempt reaches here. Scoped to a band that
  // opted in via data-wt-surface (the homepage email instance leaves it unset ⇒ no event).
  /** @param {Event} e */
  function handleSubmit(e) {
    var form = /** @type {Element} */ (e.target);
    if (!form || !form.closest || !form.closest('[data-wt-surface]')) return;
    track('WT Email Captured', { surface: surfaceOf(form) });
  }

  document.addEventListener('click', handleClick, true);
  document.addEventListener('toggle', handleToggle, true);
  document.addEventListener('submit', handleSubmit, true);
})();
